import { create } from 'zustand';
import { ApiService } from '@/shared/services/api.service';
import { isCurrentUserGuest } from '@/shared/utils/guest-detection.util';

// Single source of truth for the "you don't have this" copy — every
// entitlement-gated control in the app (tooltip on hover, snackbar on click)
// should read from here rather than writing its own wording, so a copy change
// only has to happen once.
export const ENTITLEMENT_UPGRADE_MESSAGE =
  "This isn't included in your plan. Upgrade to unlock it.";

interface EntitlementFeatureValue {
  enabled: boolean;
  limit?: number | null;
  options?: unknown[];
  reason?: string;
  /** When enabled is false: 'lock' = show locked with upgrade prompt, 'hide' = don't render. */
  display_status?: 'lock' | 'hide';
}

export interface EntitlementSet {
  version: number;
  org_pid: string;
  computed_at: string;
  products: string[];
  product_name: string | null;
  product_category: string | null;
  plans: Record<string, string>;
  plan_labels: Record<string, string>;
  features: Record<string, EntitlementFeatureValue>;
}

interface EntitlementsState {
  entitlements: EntitlementSet | null;
  loading: boolean;
  /** True once the first successful (or failed) fetch has settled. */
  fetched: boolean;
  /** True when every retry failed — entitlements stay null and isEnabled stays lenient. */
  error: boolean;
  fetchEntitlements: () => Promise<void>;
  /**
   * O(1) lookup. True while no set is loaded yet (loading or failed) so controls
   * render normally instead of flashing locked; false for a loaded set that
   * doesn't enable the key. The server is the actual enforcement boundary.
   */
  isEnabled: (key: string) => boolean;
  /**
   * Strict variant for rollout-style keys (the former feature gates): false until a
   * set is loaded, and applies to guests too (they get the visited org's set).
   */
  hasFeature: (key: string) => boolean;
  /** null = unlimited, undefined = not a quota / unknown key. */
  getLimit: (key: string) => number | null | undefined;
  /** Drop in-memory state (org switch) so the next fetch re-resolves; keeps the per-org cache. */
  reset: () => void;
}

const CACHE_KEY = 'nexgen_entitlements_cache';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes — matches the in-app refresh interval

interface CachedEntitlements {
  orgPid: string;
  entitlements: EntitlementSet;
  cachedAt: number;
}

// Scoped by orgPid (not per-user) so a browser that switches between orgs
// never serves one org's entitlements to another. Best-effort only — private
// browsing / storage-full failures just mean every load re-fetches.
function readEntitlementsCache(orgPid: string): EntitlementSet | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedEntitlements;
    if (!parsed?.entitlements || parsed.orgPid !== orgPid) return null;
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    return parsed.entitlements;
  } catch {
    return null;
  }
}

function writeEntitlementsCache(orgPid: string, entitlements: EntitlementSet): void {
  try {
    const cached: CachedEntitlements = { orgPid, entitlements, cachedAt: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // ignore — cache is best-effort only
  }
}

const RETRY_DELAYS_MS = [1000, 3000, 9000];

let _fetchPromise: Promise<void> | null = null;
let _fetchSeq = 0;
let _lastRefreshAt = 0;

/**
 * Re-pulls the set from the server, bypassing the localStorage cache, so a plan
 * change (upgrade, override, downgrade) shows without a reload. The backend
 * busts its own caches on every such change, so a refetch sees it immediately.
 * Silent on failure — the current set stays. Skips when nothing's loaded yet,
 * a first fetch is in flight, or for guests (their visit is short-lived).
 * ponytail: polled (focus + interval); swap for an entitlements.changed socket
 * event once nexgen-types/events support org-scoped broadcasts.
 */
export async function refreshEntitlements(minIntervalMs = 0): Promise<void> {
  if (!useEntitlementsStore.getState().fetched || _fetchPromise || isCurrentUserGuest()) return;
  if (Date.now() - _lastRefreshAt < minIntervalMs) return;
  _lastRefreshAt = Date.now();

  const seq = _fetchSeq;
  const orgPid = ApiService.getOrgPid() ?? '';
  try {
    const entitlements = await ApiService.get<EntitlementSet>('/me/entitlements');
    if (seq !== _fetchSeq) return;
    useEntitlementsStore.setState({ entitlements, error: false });
    writeEntitlementsCache(orgPid, entitlements);
  } catch {
    // keep the current set; the next focus/interval tick retries
  }
}

export async function fetchEntitlements(): Promise<void> {
  const state = useEntitlementsStore.getState();
  if (state.fetched) return;

  if (!_fetchPromise) {
    const promise = (async () => {
      const seq = _fetchSeq;
      // Key the cache by the org the request will actually be sent for — ApiService
      // gives session/welcome context precedence over the raw localStorage value.
      const orgPid = ApiService.getOrgPid() ?? '';

      const cached = readEntitlementsCache(orgPid);
      if (cached) {
        useEntitlementsStore.setState({ entitlements: cached, loading: false, fetched: true });
        // Render from cache instantly, but still hit the API on every page load so a
        // plan change made elsewhere shows after a refresh. Called synchronously,
        // before _fetchPromise is assigned, so refreshEntitlements doesn't skip.
        void refreshEntitlements();
        return;
      }

      useEntitlementsStore.setState({ loading: true, error: false });
      // A transient failure shouldn't decide the whole session — retry with backoff.
      for (let attempt = 0; ; attempt++) {
        try {
          const entitlements = await ApiService.get<EntitlementSet>('/me/entitlements');
          if (seq !== _fetchSeq) return;
          useEntitlementsStore.setState({ entitlements, loading: false, fetched: true });
          writeEntitlementsCache(orgPid, entitlements);
          _lastRefreshAt = Date.now();
          return;
        } catch (err) {
          if (seq !== _fetchSeq) return;
          if (attempt >= RETRY_DELAYS_MS.length) {
            // Out of retries: stay lenient (isEnabled → true with no set) rather
            // than locking a paying user out until reload. The server still enforces.
            console.error('[Entitlements] Failed to load entitlements after retries:', err);
            useEntitlementsStore.setState({ loading: false, fetched: true, error: true });
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
        }
      }
    })();
    _fetchPromise = promise;
  }

  // Cleared here, after awaiting, so every exit (cache hit included) releases it —
  // a leftover _fetchPromise would make refreshEntitlements() skip forever.
  const inFlight = _fetchPromise;
  try {
    await inFlight;
  } finally {
    if (_fetchPromise === inFlight) _fetchPromise = null;
  }
}

export const useEntitlementsStore = create<EntitlementsState>((set, get) => ({
  entitlements: null,
  loading: false,
  fetched: false,
  error: false,

  fetchEntitlements,
  // No loaded set (still loading or fetch failed), or a guest — renders the normal state; anything that matters is enforced
  // server-side. Only a loaded set can lock a control.
  // ponytail: guests ignore the host org's plan — add a guest entitlements
  // endpoint keyed by the visited org if client-only gates must apply to guests.
  isEnabled: (key: string) => {
    const { entitlements } = get();
    if (isCurrentUserGuest() || !entitlements) return true;
    return entitlements.features[key]?.enabled ?? false;
  },
  hasFeature: (key: string) => !!get().entitlements?.features[key]?.enabled,
  getLimit: (key: string) => get().entitlements?.features[key]?.limit,
  // Clears in-memory state only. The persistent cache is keyed by orgPid, so an
  // org switch already misses it — wiping it here made every startup re-fetch.
  reset: () => {
    _fetchSeq += 1;
    _fetchPromise = null;
    set({ entitlements: null, loading: false, fetched: false, error: false });
  },
}));
